import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import ms from 'ms';
import { z } from 'zod';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

import { log } from '~/utils';

import { queryKeysCompany } from './queryKeys';
import { zodEmailListResponseSchema } from './validations';

export type CompanyInvoiceAllowedSenderAddresses = z.infer<
  typeof zodEmailListResponseSchema
>;

/**
 * Fetches company invoice allowed sender addresses from the API.
 *
 * @param companyId - The ID of the company to fetch.
 * @returns The company invoice allowed sender addresses.
 * @throws Error if the API request fails.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Company/operation/get_invoice_allowed_sender_address_vadmin_company__company_id__invoice_allowed_sender_address_get
 */
export const fetchCompanyInvoiceAllowedSenderAddresses = async (
  companyId: UUID,
) => {
  try {
    const response = await vestigasApi
      .get(ENDPOINT.COMPANY.INVOICE_ALLOWED_SENDER_ADDRESS.GET(companyId), {
        validate: { response: zodEmailListResponseSchema },
      })
      .json<CompanyInvoiceAllowedSenderAddresses>();

    return response;
  } catch (error) {
    log.error({
      message: 'Error fetching invoice allowed sender addresses for company',
      error,
    });

    // Re-throw the error to propagate it to the calling function.
    throw error;
  }
};

/**
 * React Query based custom hook for getting the invoice allowed sender addresses for a company with a given companyId.
 *
 * @param companyId - The ID of the company to fetch.
 * @param options - Additional options for the useQuery hook.
 * @returns The result of the useQuery hook.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Company/operation/get_invoice_allowed_sender_address_vadmin_company__company_id__invoice_allowed_sender_address_get
 */
export const useQueryCompanyInvoiceAllowedSenderAddresses = (
  companyId: UUID,
  options?: Omit<
    UseQueryOptions<CompanyInvoiceAllowedSenderAddresses>,
    'queryKey' | 'queryFn'
  >,
) => {
  return useQuery<CompanyInvoiceAllowedSenderAddresses>({
    enabled: Boolean(companyId),
    queryFn: async () => fetchCompanyInvoiceAllowedSenderAddresses(companyId),
    queryKey: queryKeysCompany.getInvoiceAllowedSenderAddresses(companyId),
    staleTime: ms('30s'),
    ...options,
  });
};
